import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material'

import {
  Acknowledge,
  Box,
  Button,
  Checkbox,
  Container,
  ContentBox,
  Fields,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Row,
  Select,
  Stack,
  Text,
  useAlert,
} from '../../components'
import { validations } from '../../components/form'
import { CharityLogoUpload } from '../../components/logo/logoUpload'
import { mutations, queries } from '../../graphql'
import { LandingPage } from '../landing-page'
import { COUNTRY_LIST, PROVINCES_LIST } from '../../constants'
import { useNavigationGuard } from '../../hooks'

export function SignUpSuccess() {
  return (
    <Container maxWidth="sm">
      <Acknowledge.Success
        title="Welcome to GiveWise and our community of generous givers!"
        content={
          <Text.Body>
            Please check your email for a link to confirm your email and log in to your account. If you do not receive a
            confirmation email, please check your junk mail or email{' '}
            <Link href="mailto:support@givewise.ca">support@givewise.ca</Link> for assistance.
          </Text.Body>
        }
      >
        <Button to="/login">Log In</Button>
      </Acknowledge.Success>
    </Container>
  )
}

function CharityCategoryForm({ categories, myCharityCategories, onChange, setAlert }) {
  const [currentCategoryIds, setCurrentCategoryIds] = useState([])

  useEffect(() => {
    const selectedCategoryIds = categories
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter((cat) => myCharityCategories.some((selected) => selected.category.id === cat.id))
      .map((cat) => cat.id)

    setCurrentCategoryIds(selectedCategoryIds)
  }, [categories, myCharityCategories])

  const handleCategoryChange = (event) => {
    const { value } = event.target

    if (value.length === 0) {
      setAlert({
        message: 'A charity must have at least one category.',
        severity: 'error',
      })
      return
    }
    if (value.length > 4) {
      setAlert({
        message: 'You can select a maximum of 4 categories.',
        severity: 'error',
      })
      return
    }

    setCurrentCategoryIds(value)
    onChange(value)
  }

  return (
    <FormControl fullWidth>
      <InputLabel id="charityCategoryLabel">Charity Categories *</InputLabel>
      <Select
        multiple
        value={currentCategoryIds}
        onChange={handleCategoryChange}
        renderValue={(selected) =>
          selected.map((id) => categories.find((category) => category.id === id)?.name).join(', ')
        }
        aria-label="Charity Categories *"
        labelId="charityCategoryLabel"
        label="Charity Categories *"
      >
        {categories
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((category) => (
            <MenuItem key={category.id} value={category.id}>
              <Checkbox checked={currentCategoryIds.includes(category.id)} />
              <ListItemText primary={category.name} />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  )
}

function CharityDetailsConfirmForm({ formDefaultData = {}, mySocialMedia }) {
  const { id: charityId } = useParams()
  const [{ Alert: CharityAlert, alertProps: charityAlertProps }, { setAlert: setCharityAlert }] = useAlert()
  const [{ Alert: UserAlert, alertProps: userAlertProps }, { setAlert: setUserAlert }] = useAlert()
  const [charityFormChanged, setCharityFormChanged] = useState(false)
  const [userFormChanged, setUserFormChanged] = useState(false)
  const [previewGenerosityPage, setPreviewGenerosityPage] = useState({
    isPreview: false,
    previewCharityId: null,
    previewFundId: null,
  })
  const { data: fundsData } = useQuery(queries.funds.myFunds)
  const fundId = fundsData?.me?.funds?.[0]?.id
  const charityFormRef = useRef(null)
  const userFormRef = useRef(null)
  const logoUploadRef = useRef(null)
  const navigationGuard = useNavigationGuard()
  const [logoChangePending, setLogoChangePending] = useState(false)

  // Track completion of multiple submissions to avoid race conditions
  const pendingSubmissionsRef = useRef({ logo: false, charity: false, user: false })
  const hasNavigationPendingRef = useRef(false)

  // Check if all pending submissions are complete
  const checkAllSubmissionsComplete = useCallback(() => {
    const pending = pendingSubmissionsRef.current
    const allComplete = !pending.logo && !pending.charity && !pending.user

    if (allComplete && hasNavigationPendingRef.current) {
      hasNavigationPendingRef.current = false
      navigationGuard.onSubmitSuccess()
    }
  }, [navigationGuard])

  // Charity form
  const { register, handleSubmit, formState, getValues, setValue, watch, control } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      ...formDefaultData,
      description: formDefaultData.description,
      tagline: formDefaultData.tagline,
    },
  })

  // User form
  const {
    register: registerUser,
    handleSubmit: handleSubmitUser,
    formState: formStateUser,
    watch: watchUser,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      ...formDefaultData,
      firstName: formDefaultData.firstName === formDefaultData.accountName ? ' ' : formDefaultData.firstName,
      lastName: formDefaultData.lastName === formDefaultData.id ? ' ' : formDefaultData.lastName,
      tagline: formDefaultData.tagline,
    },
  })
  const { errors = {} } = formState

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type === 'change') {
        setCharityFormChanged(true)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    const subscription = watchUser((value, { name, type }) => {
      if (type === 'change') {
        setUserFormChanged(true)
      }
    })
    return () => subscription.unsubscribe()
  }, [watchUser])

  const [updateCharity, { loading }] = useMutation(mutations.charities.updateCharityCustom, {
    onCompleted: () => {
      setCharityAlert({ message: 'Charity Profile Updated Successfully', severity: 'success' })
      setCharityFormChanged(false)
      pendingSubmissionsRef.current.charity = false
      checkAllSubmissionsComplete()
    },
    onError: () => {
      setCharityAlert({ message: 'Failed to Update Charity Profile', severity: 'error' })
      pendingSubmissionsRef.current.charity = false
      hasNavigationPendingRef.current = false
      navigationGuard.onSubmitFail()
    },
    refetchQueries: ['Charity'],
  })

  const [updateUser, { loading: userLoading }] = useMutation(mutations.user.updateProfileWithoutAddress, {
    onCompleted: () => {
      setUserAlert({ message: 'User Profile Updated Successfully', severity: 'success' })
      setUserFormChanged(false)
      pendingSubmissionsRef.current.user = false
      checkAllSubmissionsComplete()
    },
    onError: () => {
      setUserAlert({ message: 'Failed to Update User Profile', severity: 'error' })
      pendingSubmissionsRef.current.user = false
      hasNavigationPendingRef.current = false
      navigationGuard.onSubmitFail()
    },
    refetchQueries: ['me'],
  })

  const [updateMyCharityDescription] = useMutation(mutations.charities.updateMyCharityDescription, {
    refetchQueries: ['getMyCharityDescription'],
  })

  const [updateMyCharityProfile] = useMutation(mutations.charities.updateMyCharityProfile, {
    refetchQueries: ['getMyCharityProfile'],
  })

  const [updateMyCharityCategories] = useMutation(mutations.charities.updateMyCharityCategories, {
    refetchQueries: ['getMyCharityCategories'],
  })

  const [logoLanguageConfirmed, setLogoLanguageConfirmed] = useState(false)
  const logoConfirmLanguage = `
    We grant GiveWise a non-exclusive, royalty-free, revocable license to use the
    Charity's logo on GiveWise's website, marketing materials, and other promotional content.
    This license is granted solely for the purpose of promoting the Charity's profile and facilitating
    donor recognition on GiveWise's platform
    `

  // Navigation guard callbacks
  const hasUnsavedChanges = charityFormChanged || userFormChanged || logoChangePending

  const submitUnsavedChanges = useCallback(() => {
    // Mark that we're starting a navigation-triggered save
    hasNavigationPendingRef.current = true

    // Mark which submissions are pending
    if (logoChangePending && logoUploadRef.current?.saveLogo) {
      pendingSubmissionsRef.current.logo = true
      logoUploadRef.current.saveLogo()
    }
    if (charityFormChanged) {
      pendingSubmissionsRef.current.charity = true
      charityFormRef.current?.requestSubmit()
    }
    if (userFormChanged) {
      pendingSubmissionsRef.current.user = true
      userFormRef.current?.requestSubmit()
    }
  }, [charityFormChanged, userFormChanged, logoChangePending])

  const discardUnsavedChanges = useCallback(() => {
    setCharityFormChanged(false)
    setUserFormChanged(false)
    setLogoChangePending(false)
    // Reset pending submission tracking to prevent stale state
    pendingSubmissionsRef.current = { logo: false, charity: false, user: false }
    hasNavigationPendingRef.current = false
    if (logoChangePending && logoUploadRef.current?.cancelLogo) {
      logoUploadRef.current.cancelLogo()
    }
  }, [logoChangePending])

  // Register/unregister unsaved changes with navigation guard
  useEffect(() => {
    if (hasUnsavedChanges) {
      navigationGuard.registerUnsavedChanges(submitUnsavedChanges, discardUnsavedChanges)
    } else {
      navigationGuard.unregisterUnsavedChanges()
    }
  }, [hasUnsavedChanges, navigationGuard, submitUnsavedChanges, discardUnsavedChanges])

  const handleLogoSelected = () => {
    // Called when user selects a logo file (but hasn't saved yet)
    setLogoChangePending(true)
  }

  const handleLogoUpdate = () => {
    // Called when logo is successfully saved
    setCharityAlert({ message: 'Logo Updated Successfully', severity: 'success' })
    setLogoLanguageConfirmed(false)
    setLogoChangePending(false) // Clear unsaved changes since logo is now saved
    pendingSubmissionsRef.current.logo = false
    checkAllSubmissionsComplete()
  }

  const handleLogoError = () => {
    // Called when logo save fails
    setLogoChangePending(false)
    pendingSubmissionsRef.current.logo = false
    hasNavigationPendingRef.current = false
    navigationGuard.onSubmitFail()
  }

  const handleLogoCanceled = () => {
    // Called when user cancels the logo upload
    setLogoChangePending(false)
  }

  const [updateSocialMediaLink] = useMutation(mutations.charities.updateSocialMediaLink, {
    refetchQueries: ['getMyCharityDescriptionSocialMedia'],
  })
  const [createSocialMediaLink] = useMutation(mutations.charities.createSocialMediaLink, {
    refetchQueries: ['getMyCharityDescriptionSocialMedia'],
  })

  const { data: allCategoriesData } = useQuery(queries.charities.getCharityCategories)

  const { data: myCharityCategoriesData } = useQuery(queries.charities.getMyCharityCategories, {
    variables: { charityId },
  })

  useEffect(() => {
    const myCharityCategories = myCharityCategoriesData?.myCharityCategories || []
    if (myCharityCategories.length > 0) {
      setValue(
        'charityCategories',
        myCharityCategories.map((cat) => cat.category.id)
      )
    }
  })

  const onInvalidCharity = () => {
    // Called when charity form validation fails
    navigationGuard.onSubmitFail()
  }

  const onSubmitCharity = ({
    mailingAddress,
    city,
    province,
    postalCode,
    country,
    contactPhoneNumber,
    emailAddress,
    websiteAddress,
  }) => {
    const updateData = {
      mailingAddress,
      city,
      province,
      postalCode,
      country,
      emailAddress,
      websiteAddress,
      contactPhoneNumber,
    }
    Object.keys(updateData).forEach((k) => {
      updateData[k] = updateData[k] ? updateData[k].trim() : ''
    })
    updateCharity({
      variables: {
        id: charityId,
        data: updateData,
      },
    })

    const newDescription = getValues('description')
    const newTagline = getValues('tagline')
    updateMyCharityDescription({
      variables: {
        data: {
          charityId,
          description: newDescription || '',
          tagline: newTagline || '',
        },
      },
    })

    const newLogoBg = getValues('logoBackgroundColor')
    updateMyCharityProfile({
      variables: {
        data: {
          charityId,
          logoBackgroundColor: newLogoBg || 'white',
        },
      },
    })

    const newCategories = getValues('charityCategories') || []
    if (newCategories.length > 0) {
      const categoryIdsAsNumbers = newCategories.map((id) => parseInt(id, 10))

      updateMyCharityCategories({
        variables: {
          charityId,
          categories: categoryIdsAsNumbers,
        },
      })
    }

    const newInstagramLink = getValues('instagramLink')
    const newFacebookLink = getValues('facebookLink')
    const newLinkedinLink = getValues('linkedinLink')

    // Update social media links
    const socialMediaLinks = [
      { id: mySocialMedia.instagramId, link: newInstagramLink, platform: 'Instagram' },
      { id: mySocialMedia.facebookId, link: newFacebookLink, platform: 'Facebook' },
      { id: mySocialMedia.linkedinId, link: newLinkedinLink, platform: 'Linkedin' },
    ]

    socialMediaLinks.forEach(({ id, link, platform }) => {
      if (link) {
        if (id) {
          // If the link exists, update it
          updateSocialMediaLink({
            variables: {
              id,
              link: link.trim(),
              platform,
              charityId,
            },
          })
        } else {
          // If the link does not exist, create it
          createSocialMediaLink({
            variables: {
              data: {
                charityId,
                link: link.trim(),
                platform,
              },
            },
          })
        }
      }
    })

    setLogoLanguageConfirmed(false)
  }

  const onInvalidUser = () => {
    // Called when user form validation fails
    navigationGuard.onSubmitFail()
  }

  const onSubmitUser = ({ title, firstName, lastName, phone }) => {
    const updateData = {
      title,
      firstName,
      lastName,
      fundPhone: phone,
    }
    Object.keys(updateData).forEach((k) => {
      updateData[k] = updateData[k] ? updateData[k].trim() : ''
    })
    updateUser({
      variables: {
        data: { ...updateData, charityId },
      },
    })
  }

  const onPreviewGenerosityPage = () => {
    console.log('Preview with charityId:', charityId, 'fundId:', fundId)

    setPreviewGenerosityPage({
      isPreview: true,
      previewCharityId: charityId,
      previewFundId: fundId,
    })
  }

  return (
    <Container maxWidth="lg">
      {previewGenerosityPage.isPreview ? (
        <>
          <Button
            onClick={() => setPreviewGenerosityPage({ isPreview: false })}
            sx={{
              minWidth: '150px',
              minHeight: '50px',
              margin: '20px',
            }}
          >
            Back to Profile
          </Button>
          <LandingPage
            charityIdProp={previewGenerosityPage.previewCharityId}
            fundIdProp={previewGenerosityPage.previewFundId}
          />
        </>
      ) : (
        <Stack spacing={4}>
          <Row style={{ margin: 0 }} justifyContent="space-between">
            <Box>
              <Text.H4 component="h1" data-testid="title">
                Account Details
              </Text.H4>
              <br />
              <Text.H7>These details will be reflected on the public facing Generosity Page.</Text.H7>
            </Box>
          </Row>
          <ContentBox style={{ marginTop: 25 }} border>
            <form ref={charityFormRef} onSubmit={handleSubmit(onSubmitCharity, onInvalidCharity)}>
              <Stack alignItems="flex-start">
                <Text.H6>Charity Logo</Text.H6>

                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <CharityLogoUpload
                    ref={logoUploadRef}
                    charityId={charityId}
                    onLogoSelected={handleLogoSelected}
                    onLogoUpdate={handleLogoUpdate}
                    onLogoError={handleLogoError}
                    onLogoCanceled={handleLogoCanceled}
                    onLogoLanguageConfirm={(confirmed) => setLogoLanguageConfirmed(confirmed)}
                    logoLanguageConfirmed={logoLanguageConfirmed}
                    logoConfirmLanguage={logoConfirmLanguage}
                  />
                </div>
                <Text.H6 sx={{ mt: 3 }}>Logo Background</Text.H6>
                <RadioGroup
                  row
                  defaultValue={formDefaultData.logoBackgroundColor || 'background.default'}
                  onChange={(e) => {
                    setValue('logoBackgroundColor', e.target.value)
                    setCharityFormChanged(true)
                  }}
                >
                  <FormControlLabel value="background.default" control={<Radio />} label="Light" />
                  <FormControlLabel value="primary.dark" control={<Radio />} label="Dark" />
                </RadioGroup>
                <Text.H6>Description</Text.H6>
                <Fields.Text
                  multiline
                  rows={7}
                  inputProps={{ 'data-testid': 'description' }}
                  label="Description *"
                  error={!!errors.Description}
                  placeholder="Share what your charity does and the impact it makes to inspire donors to give."
                  helperText={errors.description?.message}
                  {...register('description', { ...validations.required })}
                  defaultValue={formDefaultData?.description}
                  onChange={(e) => {
                    setValue('description', e.target.value)
                    setCharityFormChanged(true)
                  }}
                  sx={{ mb: 2 }}
                />
                <Fields.Text
                  multiline
                  rows={1}
                  inputProps={{ 'data-testid': 'tagline' }}
                  label="Tagline *"
                  error={!!errors.Tagline}
                  placeholder="Add a one sentence charity tagline."
                  helperText={errors.tagline?.message}
                  {...register('tagline', { ...validations.required })}
                  defaultValue={formDefaultData?.tagline || ''}
                  onChange={(e) => {
                    setValue('tagline', e.target.value)
                    setCharityFormChanged(true)
                  }}
                />
                <Box sx={{ width: '100%' }}>
                  <CharityCategoryForm
                    categories={allCategoriesData?.charityCategory || []}
                    myCharityCategories={myCharityCategoriesData?.getMyCharityCategories || []}
                    onChange={(updatedCategoryIds) => {
                      setValue('charityCategories', updatedCategoryIds)
                      setCharityFormChanged(true)
                    }}
                    setAlert={setCharityAlert}
                  />
                  <Text.AltSubtitle sx={{ ml: 1.7 }}>You can choose up to four categories</Text.AltSubtitle>
                </Box>
                <Text.H6>Charity Address</Text.H6>
                <Fields.Text
                  inputProps={{ 'data-testid': 'mailingAddress' }}
                  label="Mailing Address *"
                  error={!!errors.mailingAddress}
                  helperText={errors.mailingAddress?.message}
                  {...register('mailingAddress', { ...validations.required })}
                  onChange={() => setCharityFormChanged(true)}
                />
                <Fields.Text
                  inputProps={{ 'data-testid': 'city' }}
                  label="City *"
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  {...register('city', { ...validations.required })}
                  onChange={() => setCharityFormChanged(true)}
                />
                <FormControl fullWidth error={!!errors.province}>
                  <InputLabel id="provinceLabel">Province *</InputLabel>
                  <Controller
                    name="province"
                    control={control}
                    rules={validations.required}
                    defaultValue={formDefaultData?.province || ''}
                    render={({ field: { ref, ...field } }) => (
                      <Select
                        {...field}
                        labelId="provinceLabel"
                        label="Province *"
                        inputProps={{ 'data-testid': 'province', ref }}
                        onChange={(e) => {
                          field.onChange(e)
                          setCharityFormChanged(true)
                        }}
                      >
                        {PROVINCES_LIST.map((p) => (
                          <MenuItem key={p} value={p}>
                            {p}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.province && <FormHelperText>{errors.province.message}</FormHelperText>}
                </FormControl>
                <Fields.Text
                  inputProps={{ 'data-testid': 'postalCode' }}
                  label="Postal Code *"
                  error={!!errors.postalCode}
                  helperText={errors.postalCode?.message}
                  {...register('postalCode', { ...validations.required })}
                  onChange={() => setCharityFormChanged(true)}
                />
                <FormControl fullWidth error={!!errors.country}>
                  <InputLabel id="countryLabel">Country *</InputLabel>
                  <Controller
                    name="country"
                    control={control}
                    rules={validations.required}
                    defaultValue={formDefaultData?.country || ''}
                    render={({ field: { ref, ...field } }) => (
                      <Select
                        {...field}
                        labelId="countryLabel"
                        label="Country *"
                        inputProps={{ 'data-testid': 'country', ref }}
                        aria-label="Country *"
                        onChange={(e) => {
                          field.onChange(e)
                          setValue('country', e.target.value)
                          setCharityFormChanged(true)
                        }}
                      >
                        {COUNTRY_LIST.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>{errors.country?.message}</FormHelperText>
                </FormControl>
                <Text.H6>Charity Contact Info</Text.H6>
                <Fields.Text
                  inputProps={{ 'data-testid': 'contactPhoneNumber' }}
                  label="Charity Phone Number *"
                  error={!!errors.contactPhoneNumber}
                  helperText={errors.contactPhoneNumber?.message}
                  {...register('contactPhoneNumber', { ...validations.phoneNumber })}
                  onChange={() => setCharityFormChanged(true)}
                />
                <Fields.Text
                  inputProps={{ 'data-testid': 'emailAddress' }}
                  label="Charity Email Address"
                  error={!!errors.emailAddress}
                  helperText={errors.emailAddress?.message}
                  {...register('emailAddress', { ...validations.email })}
                  onChange={() => setCharityFormChanged(true)}
                />
                <Fields.Text
                  inputProps={{ 'data-testid': 'websiteAddress' }}
                  label="Charity Website Address"
                  error={!!errors.websiteAddress}
                  helperText={errors.websiteAddress?.message}
                  {...register('websiteAddress')}
                  onChange={() => setCharityFormChanged(true)}
                />
                <Text.H6>Social Media Links</Text.H6>
                <Fields.Text
                  inputProps={{ 'data-testid': 'Instagram' }}
                  label="Instagram Link"
                  error={!!errors.websiteAddress}
                  helperText={errors.websiteAddress?.message}
                  defaultValue={formDefaultData?.instagramLink}
                  {...register('instagramLink')}
                  onChange={() => setCharityFormChanged(true)}
                />
                <Fields.Text
                  inputProps={{ 'data-testid': 'Facebook' }}
                  label="Facebook Link"
                  error={!!errors.websiteAddress}
                  helperText={errors.websiteAddress?.message}
                  defaultValue={formDefaultData?.facebookLink}
                  {...register('facebookLink')}
                  onChange={() => setCharityFormChanged(true)}
                />
                <Fields.Text
                  inputProps={{ 'data-testid': 'Linkedin' }}
                  label="Linkedin Link"
                  error={!!errors.websiteAddress}
                  helperText={errors.websiteAddress?.message}
                  defaultValue={formDefaultData?.linkedinLink}
                  {...register('linkedinLink')}
                  onChange={() => setCharityFormChanged(true)}
                />
              </Stack>
              <Row justifyContent="right" spacing={4} sx={{ marginTop: '24px ' }}>
                <Button
                  onClick={onPreviewGenerosityPage}
                  disabled={charityFormChanged}
                  sx={{
                    minWidth: '150px',
                    minHeight: '50px',
                    backgroundColor: charityFormChanged ? '#CCCCCC' : undefined,
                  }}
                >
                  {charityFormChanged ? 'Save Changes Before Preview' : 'Preview Generosity Page'}
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !charityFormChanged}
                  data-testid="signUpButton"
                  sx={{
                    minWidth: '150px',
                    minHeight: '50px',
                    backgroundColor: charityFormChanged ? undefined : '#CCCCCC',
                  }}
                >
                  Save
                </Button>
                {errors.form && <FormHelperText error={!!errors.form}>{errors.form.message}</FormHelperText>}
              </Row>
              <CharityAlert {...charityAlertProps} />
            </form>
          </ContentBox>

          <ContentBox style={{ marginTop: 25 }} border>
            <form ref={userFormRef} onSubmit={handleSubmitUser(onSubmitUser, onInvalidUser)}>
              <Stack alignItems="flex-start">
                <Text.H6>User Details</Text.H6>
                <Text.Subtitle>
                  Please confirm the details of the user who will be accessing the charity portal.
                  <br />
                  These details will NOT be shared on the public facing charity details page.
                </Text.Subtitle>
                <Fields.Text
                  inputProps={{ 'data-testid': 'firstName' }}
                  label="First Name *"
                  error={!!formStateUser.errors.firstName}
                  helperText={formStateUser.errors.firstName?.message}
                  {...registerUser('firstName', { ...validations.required })}
                  onChange={() => setUserFormChanged(true)}
                />
                <Fields.Text
                  inputProps={{ 'data-testid': 'lastName' }}
                  label="Last Name *"
                  error={!!formStateUser.errors.lastName}
                  helperText={formStateUser.errors.lastName?.message}
                  {...registerUser('lastName', { ...validations.required })}
                  onChange={() => setUserFormChanged(true)}
                />
                <Fields.Text
                  inputProps={{ 'data-testid': 'title' }}
                  label="Job Title/Role"
                  error={!!formStateUser.errors.title}
                  helperText={formStateUser.errors.title?.message}
                  {...registerUser('title')}
                  onChange={() => setUserFormChanged(true)}
                />
                <Fields.Text
                  inputProps={{ 'data-testid': 'phone' }}
                  type="text"
                  label="Phone Number"
                  error={!!formStateUser.errors.phone}
                  helperText={formStateUser.errors?.phone?.message}
                  {...registerUser('phone', { ...validations.phoneNumber })}
                  onChange={() => setUserFormChanged(true)}
                />
              </Stack>
              <Row justifyContent="right" spacing={4} sx={{ marginTop: '24px ' }}>
                <Button
                  type="submit"
                  disabled={userLoading || formStateUser.isSubmitting || !userFormChanged}
                  data-testid="signUpButton"
                  sx={{
                    minWidth: '150px',
                    minHeight: '50px',
                    backgroundColor: userFormChanged ? undefined : '#CCCCCC',
                  }}
                >
                  Save
                </Button>
                {formStateUser.errors.form && (
                  <FormHelperText error={!!formStateUser.errors.form}>
                    {formStateUser.errors.form.message}
                  </FormHelperText>
                )}
              </Row>
              <UserAlert {...userAlertProps} />
            </form>
          </ContentBox>
        </Stack>
      )}
    </Container>
  )
}

export function CharityDetailsConfirm() {
  const { id: charityId } = useParams()
  const { data: { charity } = {}, loading: charityLoading } = useQuery(queries.charities.charity, {
    variables: { charityId },
  })
  const { data: { me } = {}, loading: loadingUser } = useQuery(queries.user.me)
  const { data: { getMyCharityDescription: myDescription } = {}, loading: descriptionLoading } = useQuery(
    queries.charities.getMyCharityDescription,
    {
      variables: { id: charityId },
    }
  )

  const { data: { getMyCharityProfile: myCharityProfile } = {} } = useQuery(queries.charities.getMyCharityProfile, {
    variables: { id: charityId },
  })
  const { data: { getMyCharityDescriptionSocialMedia: mySocialMedia } = {} } = useQuery(
    queries.charities.getMyCharityDescriptionSocialMedia,
    {
      variables: { id: charityId },
    }
  )
  const isLoading = charityLoading || loadingUser || descriptionLoading

  if (isLoading) {
    return null
  }

  if (me && charity) {
    const charityUser = me.charityUsers.find((cu) => cu.userId === me.id)
    const fundProfile = me.funds?.find((f) => f.name === `charity-${charityId}`)?.fundProfile
    const socialMediaData = mySocialMedia.reduce((acc, socialMedia) => {
      switch (socialMedia.platform) {
        case 'Instagram':
          acc.instagramId = socialMedia.id
          acc.instagramLink = socialMedia.link
          break
        case 'Facebook':
          acc.facebookId = socialMedia.id
          acc.facebookLink = socialMedia.link
          break
        case 'Linkedin':
          acc.linkedinId = socialMedia.id
          acc.linkedinLink = socialMedia.link
          break
        // Add more platforms if needed
        default:
          break
      }
      return acc
    }, {})
    return (
      <CharityDetailsConfirmForm
        formDefaultData={{
          ...charity,
          title: charityUser.title,
          firstName: me.profile.firstName,
          lastName: me.profile.lastName,
          phone: fundProfile?.phone,
          description: myDescription?.description || '',
          ...socialMediaData,
          tagline: myDescription?.tagline || '',
          logoBackgroundColor: myCharityProfile?.logoBackgroundColor || 'background.default',
        }}
        myDescription={myDescription}
        mySocialMedia={socialMediaData}
      />
    )
  }

  return ''
}
